import type {
	JavaScriptRequestSpec,
	PostgresRequestSpec,
	RequestSpec,
	SqliteRequestSpec,
} from "@moment/api-collab";

/**
 * Extract spec-level default variable values from a Postgres, SQLite, or JavaScript request spec
 * as a flat Record<string, string>. Returns an empty object for HTTP specs — HTTP
 * vars are extracted on the Rust side in `build_context` (env_fetch.rs).
 *
 * For Postgres/SQLite/JavaScript specs: extracts the `params` Kv[] field (the Variables tab).
 * These provide default values for `{{vars.X}}` / `{{params.X}}` substitution.
 */
export const extractSpecParams = (request: RequestSpec): Record<string, string> => {
	if (request.type !== "postgres" && request.type !== "sqlite" && request.type !== "javascript") {
		return {};
	}
	const spec = request as PostgresRequestSpec | SqliteRequestSpec | JavaScriptRequestSpec;
	const out: Record<string, string> = {};
	for (const kv of spec.params ?? []) {
		if (kv.enabled !== false && kv.key) {
			out[kv.key] = kv.value;
		}
	}
	return out;
};

/**
 * Merge spec-level variable defaults with caller-supplied values.
 * Caller values take precedence so programmatic callers can override
 * defaults defined in the UI.
 *
 * For HTTP requests this returns the caller params unchanged — Rust handles
 * merging spec-level `vars` into the substitution context.
 */
export const mergeParams = (
	request: RequestSpec,
	callerParams: Record<string, string>
): Record<string, string> => {
	const specParams = extractSpecParams(request);
	return { ...specParams, ...callerParams };
};
